import { type QueryRowType, type Transaction } from "@rocicorp/zero";
import { defineQuery } from "../../zero/transformer";
import { zql } from "../../zero/schema";
import type { Food } from "../food";
import { NUTRIENT_KEYS, type NutrientKey } from "./constants";
import { z } from "zod/v4";
import { NumericTarget } from "@corpuscomplete/core/common/numeric-target";
import type { Db } from "@corpuscomplete/core/drizzle";
import { nutrient } from "./nutrient.sql";
import { unit } from "../../common/unit/unit.sql";
import { eq, isNotNull } from "drizzle-orm";
import { typedEntries } from "../../utils/typed-object";
import { type FormSchemaType, zFormFloatNullable } from "@corpuscomplete/core/utils";

export namespace Nutrient {
  export const keySchema = z.enum(NUTRIENT_KEYS);
  export type Key = NutrientKey;

  const macroKeys = ["carbohydrate", "protein", "fat"] as const satisfies NutrientKey[];
  export const macroKeySchema = z.enum(macroKeys);
  export type MacroKey = (typeof macroKeys)[number];

  export const macroEnergyKeys = ["energy", ...macroKeys] as const satisfies NutrientKey[];
  export type MacroEnergyKey = (typeof macroEnergyKeys)[number];

  export const amountsSchema = z.partialRecord(keySchema, z.number());
  export type Amounts = Readonly<z.infer<typeof amountsSchema>>;
  declare const normalizedAmountsBrand: unique symbol;
  export type NormalizedAmounts = Amounts & {
    readonly [normalizedAmountsBrand]: true;
  };
  export type IdLookup = Readonly<Partial<Record<string, Key>>>;

  export const macroKcalPerGram: Record<MacroKey, number> = {
    carbohydrate: 4,
    protein: 4,
    fat: 9,
  };

  export function macroEnergyPercentages(
    nutrientAmounts: Nutrient.Amounts,
  ): Record<Nutrient.MacroKey, number> | null {
    if (
      nutrientAmounts.protein === undefined ||
      nutrientAmounts.carbohydrate === undefined ||
      nutrientAmounts.fat === undefined ||
      nutrientAmounts.energy === undefined
    ) {
      return null;
    }
    const totalEnergy = nutrientAmounts.energy;
    return {
      protein: Math.round(
        ((nutrientAmounts.protein * macroKcalPerGram.protein) / totalEnergy) * 100,
      ),
      carbohydrate: Math.round(
        ((nutrientAmounts.carbohydrate * macroKcalPerGram.carbohydrate) / totalEnergy) * 100,
      ),
      fat: Math.round(((nutrientAmounts.fat * macroKcalPerGram.fat) / totalEnergy) * 100),
    };
  }

  export function targetsSchema(args?: { requiredKeys?: readonly Key[] }) {
    const targetSchema = NumericTarget.schema({
      value: z.number().nonnegative(),
      variants: ["exact", "between", "atLeast", "atMost"],
    });
    const schema = z.partialRecord(keySchema, targetSchema);
    const requiredKeys = args?.requiredKeys;
    if (!requiredKeys || requiredKeys.length === 0) return schema;
    return schema.check((ctx) => {
      for (const key of requiredKeys) {
        if (ctx.value[key] === undefined) {
          ctx.issues.push({
            code: "custom",
            message: `Nutrient target is required for ${key}`,
            input: ctx.value,
          });
        }
      }
    });
  }

  export type Targets = z.infer<ReturnType<typeof targetsSchema>>;

  export const macroEnergyTargetsFormSchema = (type: FormSchemaType) =>
    z.object({
      enabled: z.boolean(),
      energy: zFormFloatNullable(type),
      protein: zFormFloatNullable(type),
      carbohydrate: zFormFloatNullable(type),
      fat: zFormFloatNullable(type),
    });
  export type MacroEnergyTargetsForm = z.input<ReturnType<typeof macroEnergyTargetsFormSchema>>;

  export const byKeys = defineQuery(keySchema.array().readonly(), ({ args: keys }) =>
    zql.nutrient.related("unit").where("key", "IN", keys),
  );
  export const hasKey = defineQuery(z.undefined(), () =>
    zql.nutrient.related("unit").where("key", "IS NOT", null),
  );
  export type Info = QueryRowType<typeof byKeys>;
  export type PartialInfo = { id: string; key?: Nutrient.Key | null };

  export const emptyAmounts = (): Amounts => ({});

  export const scaleAmounts = (amounts: Amounts, factor: number): Amounts => {
    const result: z.infer<typeof amountsSchema> = {};
    for (const [key, amount] of typedEntries(amounts)) {
      if (amount === undefined) {
        continue;
      }
      result[key] = amount * factor;
    }
    return result;
  };

  const PRECISION = 3;
  export const TRACE_SENTINEL = 0.0001;
  const ROUNDING_THRESHOLD = 0.0005;

  export const normalizeAmounts = (amounts: Amounts): NormalizedAmounts => {
    const result: z.infer<typeof amountsSchema> = {};
    for (const [nutrientKey, value] of typedEntries(amounts)) {
      const isRequired = (macroEnergyKeys as readonly Key[]).includes(nutrientKey);

      if (value === undefined) {
        if (isRequired) result[nutrientKey] = 0;
        continue;
      }

      if (!Number.isFinite(value)) {
        if (isRequired) result[nutrientKey] = 0;
        continue;
      }

      if (value > 0 && value < ROUNDING_THRESHOLD) {
        result[nutrientKey] = TRACE_SENTINEL;
        continue;
      }

      const rounded = Math.round(value * 10 ** PRECISION) / 10 ** PRECISION;
      if (rounded === 0 && !isRequired) continue;

      result[nutrientKey] = rounded;
    }
    return result as NormalizedAmounts;
  };

  export const addAmounts = (base: Amounts, extra: Amounts): Amounts => {
    const result: z.infer<typeof amountsSchema> = { ...base };
    for (const [key, amount] of typedEntries(extra)) {
      if (amount === undefined) {
        continue;
      }
      result[key] = (result[key] ?? 0) + amount;
    }
    return result;
  };

  export const addFoodItems = (
    base: Amounts,
    items: readonly Pick<Food.Item, "amount" | "serving">[],
  ): Amounts => {
    const result: z.infer<typeof amountsSchema> = { ...base };
    for (const item of items) {
      for (const [key, amount] of typedEntries(item.serving.nutrients)) {
        if (amount === undefined) {
          continue;
        }
        result[key] = (result[key] ?? 0) + amount * item.amount;
      }
    }
    return result;
  };

  export const nutrientsToAmounts = (
    nutrients: readonly (
      | { nutrientId: string; amount: number }
      | { nutrientId: string; amountPer100g: number }
    )[],
    lookup: IdLookup,
    factor = 1,
  ): Amounts => {
    const result: z.infer<typeof amountsSchema> = {};
    for (const nutrient of nutrients) {
      const key = lookup[nutrient.nutrientId];
      if (!key) {
        continue;
      }
      const amount = "amount" in nutrient ? nutrient.amount : nutrient.amountPer100g;
      result[key] = (result[key] ?? 0) + amount * factor;
    }
    return result;
  };

  export const createIdLookup = (nutrients: readonly PartialInfo[]): IdLookup => {
    const lookup: Record<string, Key> = {};
    for (const nutrient of nutrients) {
      if (!nutrient.key) {
        continue;
      }
      lookup[nutrient.id] = nutrient.key;
    }
    return lookup;
  };

  export const createIdLookupForKeys = async (tx: Transaction): Promise<IdLookup> => {
    const nutrients = await tx.run(zql.nutrient.where("key", "IS NOT", null));
    return createIdLookup(nutrients);
  };

  const amountsToRows = (amounts: Amounts, nutrients: readonly PartialInfo[]) => {
    return nutrients.flatMap((nutrient) => {
      if (!nutrient.key) {
        return [];
      }
      return [{ nutrientId: nutrient.id, amount: amounts[nutrient.key] ?? 0 }];
    });
  };

  export const amountsToRowsForKeys = async (tx: Transaction, amounts: Amounts) => {
    const nutrients = await tx.run(zql.nutrient.where("key", "IS NOT", null));
    return amountsToRows(amounts, nutrients);
  };

  export const amountsToInfo = <N extends PartialInfo>(
    amounts: Amounts,
    nutrients: readonly N[],
    keyOrder?: readonly Key[],
  ) => {
    const nutrientByKey = new Map(
      nutrients.flatMap((nutrient) => (nutrient.key ? [[nutrient.key, nutrient]] : [])),
    );
    const keys = keyOrder ?? Array.from(nutrientByKey.keys());
    return keys.flatMap((key) => {
      const nutrient = nutrientByKey.get(key);
      if (!nutrient) {
        return [];
      }
      return [{ nutrient, amount: amounts[key] ?? 0 }];
    });
  };

  export const normalizeTargets = (targets: Targets): Targets => {
    const result: Targets = {};
    for (const [key, target] of typedEntries(targets)) {
      if (!target) continue;
      switch (target.type) {
        case "exact":
        case "atLeast":
        case "atMost":
          result[key] = { type: target.type, value: normalizeTargetNumber(target.value) };
          break;
        case "between":
          result[key] = {
            type: "between",
            min: normalizeTargetNumber(target.min),
            max: normalizeTargetNumber(target.max),
          };
          break;
      }
    }
    return result;
  };

  export type CheckAmountsOptions = {
    defaultTargetOptions?: NumericTarget.CheckOptions;
    targetOptionsByKey?: Partial<Record<Key, NumericTarget.CheckOptions>>;
  };

  export type CheckAmountsResult = { ok: true } | { ok: false; failedKeys: Key[] };

  export function checkAmounts(
    amounts: Amounts,
    targets: Targets,
    options?: CheckAmountsOptions,
  ): CheckAmountsResult {
    const failedKeys: Key[] = [];

    for (const [key, target] of typedEntries(targets)) {
      if (!target) {
        continue;
      }

      const targetOptions = options?.targetOptionsByKey?.[key] ?? options?.defaultTargetOptions;
      if (!NumericTarget.check(amounts[key] ?? 0, target, targetOptions)) {
        failedKeys.push(key);
      }
    }

    return failedKeys.length === 0 ? { ok: true } : { ok: false, failedKeys };
  }

  export function targetsToMacroEnergyTargetsForm(
    targets: Targets | null | undefined,
  ): MacroEnergyTargetsForm {
    return {
      enabled: targets != null,
      energy: targetToString(targets?.energy),
      protein: targetToString(targets?.protein),
      carbohydrate: targetToString(targets?.carbohydrate),
      fat: targetToString(targets?.fat),
    };
  }

  export function macroEnergyTargetsFormToTargets(
    form: MacroEnergyTargetsForm,
    existingTargets?: Targets | null,
  ): Targets | null {
    if (!form.enabled) {
      return null;
    }

    const parsedTargets: Targets = { ...existingTargets };
    for (const key of macroEnergyKeys) {
      const target = NumericTarget.Exact.fromString(form[key]);
      if (target) {
        parsedTargets[key] = target;
      } else {
        delete parsedTargets[key];
      }
    }

    return Object.keys(parsedTargets).length > 0 ? parsedTargets : null;
  }

  export const fetchNutrientsWithKeys = async (db: Db): Promise<Nutrient.Info[]> => {
    const result = await db
      .select()
      .from(nutrient)
      .innerJoin(unit, eq(nutrient.unitKey, unit.key))
      .where(isNotNull(nutrient.key));
    return result.map(({ nutrient, unit }) => ({
      ...nutrient,
      unit,
    }));
  };
}

function normalizeTargetNumber(value: number): number {
  const finite = Number.isFinite(value) ? value : 0;
  return Math.round(finite * 10 ** 3) / 10 ** 3;
}

function targetToString(
  target: Nutrient.Targets[keyof Nutrient.Targets] | null | undefined,
): string {
  if (!target || target.type !== "exact") {
    return "";
  }

  return NumericTarget.Exact.asString(target);
}
